import { useEffect, useEffectEvent, useRef, useState } from 'react';

const useScrollToBottom = () => {
  const containerRef = useRef<HTMLElement>(null);
  const scrollAnchorRef = useRef<HTMLElement>(null);
  const scrollLocked = useRef(true);
  const [isNearBottom, setIsNearBottom] = useState(false);

  const scrollToAnchor = (behavior: ScrollBehavior = 'auto') => {
    const container = containerRef.current;
    const scrollAnchor = scrollAnchorRef.current;

    if (!container || !scrollAnchor) return;

    container.scrollTo({
      top: scrollAnchor.offsetTop,
      behavior,
    });
  };

  const scrollToBottom = () => {
    if (!isNearBottom) {
      scrollToAnchor('smooth');
    }
  };

  const handleScroll = useEffectEvent(() => {
    const container = containerRef.current;
    const scrollAnchor = scrollAnchorRef.current;

    if (!container || !scrollAnchor) return;

    if (
      scrollAnchor.getBoundingClientRect().top >
      container.getBoundingClientRect().bottom
    ) {
      scrollLocked.current = false;
    }
  });

  useEffect(() => {
    const container = containerRef.current;
    const scrollAnchor = scrollAnchorRef.current;

    if (!container || !scrollAnchor) return () => {};

    container.addEventListener('scroll', handleScroll);

    scrollToAnchor('smooth');

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearBottom(true);
          scrollLocked.current = true;
        } else {
          setIsNearBottom(false);
          if (scrollLocked.current) {
            scrollToAnchor();
          }
        }
      },
      { root: container },
    );

    observer.observe(scrollAnchor);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return {
    scrollToBottom,
    isNearBottom,
    setContainerRef: containerRef,
    setScrollAnchorRef: scrollAnchorRef,
  };
};

export default useScrollToBottom;
